import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  IImageBreakpoint,
  IMedia,
  MediaObjectFit,
  MediaType,
} from '@epicuro-next/ataraxia/models';

const SLOW_X_ROTATE = -5;
const GRADUAL_STOP = 0.95;

/**
 * Component responsible for rendering Media elements:
 * Video, images, images 360
 */
@Component({
  selector: 'atx-media-360',
  templateUrl: './media-360.component.html',
  styleUrls: ['./media-360.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class Media360Component implements AfterViewInit, OnDestroy {
  @Input() public media: IMedia;
  @Input() public fallbackImageUrl?: string;
  @Input() public objectFit: MediaObjectFit = MediaObjectFit.CONTAIN;

  @Input() public maxWidth?: number;
  @Input() public imageBreakpoints?: IImageBreakpoint[];

  @Input() public rotateOnReady = false;
  @Output() public imageClick = new EventEmitter<string>();

  private buffers = [0, 0];
  private speed = [0, 0];
  private rotationInterval: NodeJS.Timeout;

  public currentImageIndex = 0;
  public spinning = false;

  public MediaType = MediaType;

  constructor(private cdr: ChangeDetectorRef) {}

  public ngOnDestroy() {
    clearInterval(this.rotationInterval);
  }

  public ngAfterViewInit() {
    if (this.rotateOnReady) {
      this.startRotation();
    }
  }

  public spinningClick(event: Event) {
    event.stopImmediatePropagation();
    event.preventDefault();

    if (this.spinning) {
      this.finishRotation();
    } else {
      this.startRotation();
    }
  }

  public onImageClick(imageUrl: string) {
    this.finishRotation();
    this.imageClick.emit(imageUrl);
  }

  private rotate(xDiff: number, yDiff: number) {
    this.buffers[0] += xDiff;
    this.buffers[1] += yDiff;

    this.speed = [xDiff, yDiff];

    if (this.buffers[0] === 0) {
      return;
    }

    const pixelsPerFrame = 360 / (this.media.urls ?? []).length;
    const direction = this.buffers[0] < 0 ? -1 : 1;

    let absBuffer = this.buffers[0] * direction;
    let amount = 0;

    while (absBuffer > pixelsPerFrame) {
      amount++;
      absBuffer -= pixelsPerFrame;
    }

    if (amount > 0) {
      this.advanceFrame(direction, amount);
    }

    this.buffers[0] = absBuffer * direction;
  }

  private advanceFrame(direction: number, amount: number) {
    this.currentImageIndex += direction * amount;
    while (this.currentImageIndex < (this.media.urls ?? []).length) {
      this.currentImageIndex += (this.media.urls ?? []).length;
    }

    this.currentImageIndex =
      this.currentImageIndex % (this.media.urls ?? []).length;
    this.cdr.markForCheck();
  }

  private startRotation() {
    this.spinning = true;
    clearInterval(this.rotationInterval);
    setTimeout(() => {
      this.rotationInterval = setInterval(() => {
        this.rotate(SLOW_X_ROTATE, 0);
      }, 70);
    });
  }

  private finishRotation() {
    clearInterval(this.rotationInterval);

    this.rotationInterval = setInterval(() => {
      this.speed[0] = this.speed[0] * GRADUAL_STOP;
      this.speed[1] = this.speed[1] * GRADUAL_STOP;

      this.rotate(this.speed[0], this.speed[1]);

      if (
        this.speed[0] > -1 &&
        this.speed[0] < 1 &&
        this.speed[1] > -1 &&
        this.speed[1] < 1
      ) {
        this.spinning = false;
        clearInterval(this.rotationInterval);
      }
    }, 10);
  }
}
